// All six Financial Report sub-tab panels live in a single file for ease of
// co-location; each panel is a thin wrapper around a fetch hook + charts/table.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ComposedChart, PieChart, Pie, Cell,
} from 'recharts';
import {
  getCashierOverview, getDepositReport, getWithdrawalReport,
  getCashierMix, getFTDReport, getPlayerCashier,
} from '../../api/client';
import { formatAmount, formatNumber, formatCurrency } from '../../lib/format';
import { metricTip } from '../../lib/metric-info';
import { Th } from '../Th';
import { siteParam, siteName, useSites } from '../SiteFilter';
import { currencySymbol } from '../CurrencySelector';
import { paymentMethodTypeBadgeClasses, paymentMethodTypeLabel } from '../PaymentMethodTypeFilter';
import { ExportButtons } from '../ExportButtons';
import type { FinancialFilters } from './useFinancialFilters';
import { DrillDownModal, type DrillDownScope } from './DrillDownModal';

// ---------- shared utilities ----------

function useFetch<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(String(err?.message || err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

function fmtMoney(symbol: string, v: number): string {
  return `${symbol}${formatAmount(v || 0)}`;
}

// Format a raw amount using the ISO currency code (for BASE mode where each
// row is in its own native currency). Locale-aware via the shared helper.
function fmtCurrency(code: string, v: number): string {
  return formatCurrency(v || 0, code);
}

// Render the bucket date depending on the active granularity.
// Backend returns the Monday for weekly and the first of month for monthly
// (see goldDateFunc in kpi_repo.go), both as plain ISO strings — we humanize
// them here so the Date column doesn't look like "2026-03-23" when the user
// picked "Weekly".
function fmtBucket(iso: string, granularity: string): string {
  if (!iso) return '';
  // Parse as local date to avoid TZ shifting "2026-03-01" to Feb 28.
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  if (granularity === 'monthly') {
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  }
  if (granularity === 'weekly') {
    const end = new Date(dt);
    end.setDate(end.getDate() + 6);
    const sameMonth = dt.getMonth() === end.getMonth();
    const left  = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const right = end.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
    return `${left} – ${right}`;
  }
  return iso;
}

// Inline cell that shows the site's human-readable name with the numeric id in
// a native browser tooltip. Reads from SiteContext — no module-level cache.
const SiteCell: React.FC<{ id: number | undefined }> = ({ id }) => {
  const { allSites } = useSites();
  if (id == null) return <>{''}</>;
  const name = siteName(allSites, id);
  return <span title={`ID ${id}`} className="cursor-help">{name}</span>;
};

// Clickable number that opens the drill-down modal. Used wherever a cell
// represents an aggregate we can break down into individual players/events
// (unique depositors, unique players, FTD count, …).
const DrillLink: React.FC<{ onClick: () => void; children: React.ReactNode; title?: string }> = ({ onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="cursor-pointer font-medium text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
  >
    {children}
  </button>
);

// ---------- Overview ----------

interface OverviewRow {
  date: string;
  site_id: number;
  currency?: string;
  total_deposits: number;
  total_withdrawals: number;
  net_cash: number;
  net_cash_pct: number;
  deposit_count: number;
  withdrawal_count: number;
  unique_depositors: number;
}

interface OverviewTotals {
  currency?: string;
  total_deposits: number;
  total_withdrawals: number;
  net_cash: number;
  net_cash_pct: number;
  deposit_count: number;
  withdrawal_count: number;
  unique_depositors: number;
}

interface OverviewResponse {
  rows: OverviewRow[];
  totals: OverviewTotals[];
}

export const FinancialOverviewPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  const [drillScope, setDrillScope] = useState<DrillDownScope | null>(null);
  const { data, loading, error } = useFetch<{ data: OverviewResponse }>(
    () => getCashierOverview({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      payment_method_type: filters.pmType,
      granularity: filters.granularity,
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.pmType, filters.granularity],
  );
  const rows = data?.data?.rows ?? [];
  const totals = data?.data?.totals ?? [];

  // Aggregate per date across sites for the combined chart.
  const byDate = new Map<string, { date: string; deposits: number; withdrawals: number; net: number }>();
  for (const r of rows) {
    const prev = byDate.get(r.date) ?? { date: r.date, deposits: 0, withdrawals: 0, net: 0 };
    prev.deposits += r.total_deposits || 0;
    prev.withdrawals += r.total_withdrawals || 0;
    prev.net += r.net_cash || 0;
    byDate.set(r.date, prev);
  }
  const chartData = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));

  // KPI card totals come from the dedicated period-wide aggregate in the
  // backend (single uniqMerge over the whole window) so cross-bucket
  // depositors are not double-counted. In EUR/USD mode totals has a single
  // entry; in BASE mode there is one entry per currency.
  const isBase = filters.currencyMode === 'BASE';
  const singleTotals = !isBase ? totals[0] : undefined;
  const totalDeposits    = singleTotals?.total_deposits    ?? 0;
  const totalWithdrawals = singleTotals?.total_withdrawals ?? 0;
  const netCash          = singleTotals?.net_cash          ?? 0;
  const uniqueDepositors = singleTotals?.unique_depositors ?? 0;

  const currencyGroups = isBase
    ? [...totals].sort((a, b) => (b.total_deposits || 0) - (a.total_deposits || 0))
    : [];

  return (
    <div className="space-y-6">
      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">{error}</div>}
      {/* KPI cards — single group in EUR/USD, one group per currency in BASE */}
      {isBase ? (
        <div className="space-y-4">
          {currencyGroups.length === 0 && (
            <div className="text-sm text-slate-500">{t('financialReport.noDataAvailable')}</div>
          )}
          {currencyGroups.map((g) => {
            const code = g.currency || 'BASE';
            return (
              <div key={code}>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{code}</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <KpiCard label={t('financialReport.kpiNetCash', { defaultValue: 'Net Cash' })}            value={fmtCurrency(code, g.net_cash)}           accent={g.net_cash >= 0 ? 'green' : 'red'} />
                  <KpiCard label={t('financialReport.kpiTotalDeposits', { defaultValue: 'Total Deposits' })} value={fmtCurrency(code, g.total_deposits)}    accent="green" />
                  <KpiCard label={t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Total Withdrawals' })} value={fmtCurrency(code, g.total_withdrawals)} accent="red" />
                  <KpiCard label={t('financialReport.kpiUniqueDepositors', { defaultValue: 'Unique Depositors' })} value={formatNumber(g.unique_depositors || 0)} accent="blue" />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label={t('financialReport.kpiNetCash', { defaultValue: 'Net Cash' })}            value={fmtMoney(symbol, netCash)} accent={netCash >= 0 ? 'green' : 'red'} />
          <KpiCard label={t('financialReport.kpiTotalDeposits', { defaultValue: 'Total Deposits' })} value={fmtMoney(symbol, totalDeposits)} accent="green" />
          <KpiCard label={t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Total Withdrawals' })} value={fmtMoney(symbol, totalWithdrawals)} accent="red" />
          <KpiCard label={t('financialReport.kpiUniqueDepositors', { defaultValue: 'Unique Depositors' })} value={formatNumber(uniqueDepositors)} accent="blue" />
        </div>
      )}

      {/* Combined chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
          {t('financialReport.chartCombinedTitle', { defaultValue: 'Deposits vs Withdrawals vs NetCash' })}
        </h2>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => fmtMoney(symbol, v)}
            />
            <Legend />
            <Bar dataKey="deposits"    name={t('financialReport.kpiTotalDeposits',    { defaultValue: 'Deposits' })}    fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="withdrawals" name={t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Withdrawals' })} fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="net" name={t('financialReport.kpiNetCash', { defaultValue: 'Net Cash' })} stroke="#3b82f6" strokeWidth={3} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Table per site/date */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[
                { l: t('financialReport.columnDate'), i: 'date' },
                { l: t('financialReport.columnSite'), i: 'site' },
                ...(filters.currencyMode === 'BASE' ? [{ l: t('financialReport.columnCurrency'), i: 'currency' }] : []),
                { l: t('financialReport.kpiTotalDeposits', { defaultValue: 'Deposits' }), i: 'deposits' },
                { l: t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Withdrawals' }), i: 'withdrawals' },
                { l: t('financialReport.columnNetCash', { defaultValue: 'Net Cash' }), i: 'netCash' },
                { l: t('financialReport.columnNetCashPct', { defaultValue: 'Net %' }), i: 'holdPct' },
                { l: t('financialReport.kpiUniqueDepositors', { defaultValue: 'Depositors' }), i: 'uniqueDepositors' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noDataAvailable')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(r.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>
                {filters.currencyMode === 'BASE' && <td className="px-4 py-3 text-slate-900 dark:text-white">{r.currency || ''}</td>}
                <td className="px-4 py-3 text-emerald-700 dark:text-emerald-400">{fmtMoney(symbol, r.total_deposits)}</td>
                <td className="px-4 py-3 text-red-700 dark:text-red-400">{fmtMoney(symbol, r.total_withdrawals)}</td>
                <td className={`px-4 py-3 font-semibold ${r.net_cash >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-red-700 dark:text-red-400'}`}>{fmtMoney(symbol, r.net_cash)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber((r.net_cash_pct || 0) * 100, 1)}%</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">
                  {(r.unique_depositors || 0) > 0 ? (
                    <DrillLink
                      title={t('financialReport.drillDownDepositors', { defaultValue: 'Show depositors' })}
                      onClick={() => setDrillScope({
                        dateFrom: r.date,
                        dateTo: r.date,
                        siteIds: [r.site_id],
                        currencyMode: filters.currencyMode,
                        pmType: filters.pmType,
                        title: `${siteName(allSites, r.site_id)} · ${r.date} · ${t('financialReport.kpiUniqueDepositors', { defaultValue: 'Depositors' })}`,
                      })}
                    >
                      {formatNumber(r.unique_depositors || 0)}
                    </DrillLink>
                  ) : formatNumber(r.unique_depositors || 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DrillDownModal open={drillScope !== null} onClose={() => setDrillScope(null)} scope={drillScope} />
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; accent: 'green' | 'red' | 'blue' | 'amber' | 'purple' }> = ({ label, value, accent }) => {
  const colors = {
    green:  'text-emerald-700 dark:text-emerald-400',
    red:    'text-red-700 dark:text-red-400',
    blue:   'text-blue-700 dark:text-blue-400',
    amber:  'text-amber-700 dark:text-amber-400',
    purple: 'text-purple-700 dark:text-purple-400',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${colors[accent]}`}>{value}</p>
    </div>
  );
};

// ---------- Deposits / Withdrawals (legacy behaviour, retained) ----------

interface DepositRow {
  date: string;
  site_id: number;
  currency?: string;
  payment_method?: string;
  payment_method_type?: string;
  total_deposits: number;
  deposit_count: number;
  unique_depositors: number;
  avg_deposit: number;
}

interface WithdrawalRow {
  date: string;
  site_id: number;
  currency?: string;
  payment_method?: string;
  payment_method_type?: string;
  total_withdrawals: number;
  withdrawal_count: number;
  cancelled_withdrawals: number;
  cancelled_count: number;
  rejected_withdrawals: number;
  rejected_count: number;
}

export const FinancialDepositsPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  const [drillScope, setDrillScope] = useState<DrillDownScope | null>(null);
  const { data, loading } = useFetch<{ data: DepositRow[] }>(
    () => getDepositReport({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      payment_method_type: filters.pmType,
      granularity: filters.granularity,
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.pmType, filters.granularity],
  );
  const rows = data?.data ?? [];
  const totalAmount = rows.reduce((s, r) => s + (r.total_deposits || 0), 0);
  const totalCount  = rows.reduce((s, r) => s + (r.deposit_count || 0), 0);
  const isBase = filters.currencyMode === 'BASE';
  const depByCurrency = new Map<string, { amount: number; count: number }>();
  if (isBase) {
    for (const r of rows) {
      const code = r.currency || 'BASE';
      const prev = depByCurrency.get(code) ?? { amount: 0, count: 0 };
      prev.amount += r.total_deposits || 0;
      prev.count  += r.deposit_count  || 0;
      depByCurrency.set(code, prev);
    }
  }
  const depCurrencyGroups = Array.from(depByCurrency.entries()).sort((a, b) => b[1].amount - a[1].amount);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButtons reportType="deposits" dateFrom={filters.dateFrom} dateTo={filters.dateTo} siteIds={siteParam(allSites, filters.siteIds)} />
      </div>
      {isBase ? (
        <div className="space-y-4">
          {depCurrencyGroups.map(([code, g]) => (
            <div key={code}>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{code}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <KpiCard label={t('financialReport.totalDeposited')}    value={fmtCurrency(code, g.amount)} accent="green" />
                <KpiCard label={t('financialReport.totalTransactions')} value={formatNumber(g.count)}   accent="blue" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <KpiCard label={t('financialReport.totalDeposited')}    value={fmtMoney(symbol, totalAmount)} accent="green" />
          <KpiCard label={t('financialReport.totalTransactions')} value={formatNumber(totalCount)}   accent="blue" />
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('financialReport.depositsByDay')}</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }} formatter={(v: number) => fmtMoney(symbol, v)} />
            <Legend />
            <Bar dataKey="total_deposits" name={t('financialReport.chartLegendAmount')} fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: t('financialReport.columnDate'), i: 'date' }, { l: t('financialReport.columnSite'), i: 'site' },
                ...(filters.currencyMode === 'BASE' ? [{ l: t('financialReport.columnCurrency'), i: 'currency' }] : []),
                { l: t('financialReport.columnPaymentMethod', { defaultValue: 'Payment Method' }), i: 'paymentMethod' },
                { l: t('financialReport.columnPaymentType', { defaultValue: 'Type' }), i: 'paymentType' },
                { l: t('financialReport.columnAmount'), i: 'deposits' }, { l: t('financialReport.columnCount'), i: 'transactionCount' },
                { l: t('financialReport.columnUniqueDepositors'), i: 'uniqueDepositors' }, { l: t('financialReport.columnAvgDeposit'), i: 'avgDeposit' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noDataAvailable')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(r.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>
                {filters.currencyMode === 'BASE' && <td className="px-4 py-3 text-slate-900 dark:text-white">{r.currency || ''}</td>}
                <td className="px-4 py-3 text-slate-900 dark:text-white">{r.payment_method || ''}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(r.payment_method_type)}`}>
                    {paymentMethodTypeLabel(r.payment_method_type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(symbol, r.total_deposits)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{r.deposit_count || 0}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">
                  {(r.unique_depositors || 0) > 0 ? (
                    <DrillLink
                      title={t('financialReport.drillDownDepositors', { defaultValue: 'Show depositors' })}
                      onClick={() => setDrillScope({
                        dateFrom: r.date,
                        dateTo: r.date,
                        siteIds: [r.site_id],
                        currencyMode: filters.currencyMode,
                        pmType: (r.payment_method_type as DrillDownScope['pmType']) || filters.pmType,
                        eventTypes: ['deposit_completed'],
                        title: `${siteName(allSites, r.site_id)} · ${r.date} · ${t('financialReport.kpiUniqueDepositors', { defaultValue: 'Depositors' })}`,
                      })}
                    >
                      {formatNumber(r.unique_depositors || 0)}
                    </DrillLink>
                  ) : (r.unique_depositors || 0)}
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(symbol, r.avg_deposit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DrillDownModal open={drillScope !== null} onClose={() => setDrillScope(null)} scope={drillScope} />
    </div>
  );
};

export const FinancialWithdrawalsPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  const { data, loading } = useFetch<{ data: WithdrawalRow[] }>(
    () => getWithdrawalReport({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      payment_method_type: filters.pmType,
      granularity: filters.granularity,
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.pmType, filters.granularity],
  );
  const rows = data?.data ?? [];
  const totalAmount    = rows.reduce((s, r) => s + (r.total_withdrawals || 0), 0);
  const totalCount     = rows.reduce((s, r) => s + (r.withdrawal_count || 0), 0);
  const totalCancelled = rows.reduce((s, r) => s + (r.cancelled_withdrawals || 0), 0);
  const totalRejected  = rows.reduce((s, r) => s + (r.rejected_withdrawals || 0), 0);
  const isBase = filters.currencyMode === 'BASE';
  const wdByCurrency = new Map<string, { amount: number; count: number; cancelled: number; rejected: number }>();
  if (isBase) {
    for (const r of rows) {
      const code = r.currency || 'BASE';
      const prev = wdByCurrency.get(code) ?? { amount: 0, count: 0, cancelled: 0, rejected: 0 };
      prev.amount    += r.total_withdrawals || 0;
      prev.count     += r.withdrawal_count  || 0;
      prev.cancelled += r.cancelled_withdrawals || 0;
      prev.rejected  += r.rejected_withdrawals  || 0;
      wdByCurrency.set(code, prev);
    }
  }
  const wdCurrencyGroups = Array.from(wdByCurrency.entries()).sort((a, b) => b[1].amount - a[1].amount);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButtons reportType="withdrawals" dateFrom={filters.dateFrom} dateTo={filters.dateTo} siteIds={siteParam(allSites, filters.siteIds)} />
      </div>
      {isBase ? (
        <div className="space-y-4">
          {wdCurrencyGroups.map(([code, g]) => (
            <div key={code}>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{code}</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard label={t('financialReport.totalWithdrawn')}    value={fmtCurrency(code, g.amount)} accent="red" />
                <KpiCard label={t('financialReport.totalTransactions')} value={formatNumber(g.count)}   accent="blue" />
                <KpiCard label={t('financialReport.cancelledWithdrawn', { defaultValue: 'Cancelled' })} value={fmtCurrency(code, g.cancelled)} accent="amber" />
                <KpiCard label={t('financialReport.rejectedWithdrawn', { defaultValue: 'Rejected' })}   value={fmtCurrency(code, g.rejected)}  accent="purple" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label={t('financialReport.totalWithdrawn')}    value={fmtMoney(symbol, totalAmount)} accent="red" />
          <KpiCard label={t('financialReport.totalTransactions')} value={formatNumber(totalCount)}   accent="blue" />
          <KpiCard label={t('financialReport.cancelledWithdrawn', { defaultValue: 'Cancelled' })} value={fmtMoney(symbol, totalCancelled)} accent="amber" />
          <KpiCard label={t('financialReport.rejectedWithdrawn', { defaultValue: 'Rejected' })}   value={fmtMoney(symbol, totalRejected)}  accent="purple" />
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('financialReport.withdrawalsByDay')}</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }} formatter={(v: number) => fmtMoney(symbol, v)} />
            <Legend />
            <Bar dataKey="total_withdrawals" name={t('financialReport.chartLegendAmount')} fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cancelled_withdrawals" name={t('financialReport.cancelledWithdrawn', { defaultValue: 'Cancelled' })} fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="rejected_withdrawals" name={t('financialReport.rejectedWithdrawn', { defaultValue: 'Rejected' })} fill="#a855f7" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: t('financialReport.columnDate'), i: 'date' }, { l: t('financialReport.columnSite'), i: 'site' },
                ...(filters.currencyMode === 'BASE' ? [{ l: t('financialReport.columnCurrency'), i: 'currency' }] : []),
                { l: t('financialReport.columnPaymentMethod', { defaultValue: 'Payment Method' }), i: 'paymentMethod' },
                { l: t('financialReport.columnPaymentType', { defaultValue: 'Type' }), i: 'paymentType' },
                { l: t('financialReport.columnAmount'), i: 'withdrawals' }, { l: t('financialReport.columnCount'), i: 'transactionCount' },
                { l: t('financialReport.columnCancelled', { defaultValue: 'Cancelled' }), i: 'cancelledWithdrawals' },
                { l: t('financialReport.columnRejected', { defaultValue: 'Rejected' }), i: 'rejectedWithdrawals' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noDataAvailable')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(r.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>
                {filters.currencyMode === 'BASE' && <td className="px-4 py-3 text-slate-900 dark:text-white">{r.currency || ''}</td>}
                <td className="px-4 py-3 text-slate-900 dark:text-white">{r.payment_method || ''}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(r.payment_method_type)}`}>
                    {paymentMethodTypeLabel(r.payment_method_type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(symbol, r.total_withdrawals)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{r.withdrawal_count || 0}</td>
                <td className="px-4 py-3 text-amber-700 dark:text-amber-400">{fmtMoney(symbol, r.cancelled_withdrawals || 0)}</td>
                <td className="px-4 py-3 text-purple-700 dark:text-purple-400">{fmtMoney(symbol, r.rejected_withdrawals || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Mix ----------

interface MixRow {
  payment_method_type: string;
  payment_method?: string;
  site_id?: number;
  total_amount: number;
  transaction_count: number;
  unique_players: number;
  pct_of_total: number;
}

// Colors aligned with paymentMethodTypeBadgeClasses (the Tailwind palette).
const PM_TYPE_COLORS: Record<string, string> = {
  electronic:        '#3b82f6', // blue-500
  network_transfer:  '#f59e0b', // amber-500
  manual_adjustment: '#ef4444', // red-500
  cash_store:        '#ec4899', // pink-500
  unknown:           '#64748b', // slate-500
};

export const FinancialMixPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  const [drillScope, setDrillScope] = useState<DrillDownScope | null>(null);
  const dimensions = filters.dimensions.filter((d) => d === 'payment_method' || d === 'site');

  const { data, loading } = useFetch<{ data: MixRow[] }>(
    () => getCashierMix({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      payment_method_type: filters.pmType,
      dimensions: dimensions.join(','),
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.pmType, dimensions.join(',')],
  );
  const rows = data?.data ?? [];

  // Donut needs one slice per payment_method_type only — aggregate when chips add extra dims.
  const donutMap = new Map<string, number>();
  for (const r of rows) {
    donutMap.set(r.payment_method_type, (donutMap.get(r.payment_method_type) || 0) + (r.total_amount || 0));
  }
  const donutData = Array.from(donutMap.entries()).map(([type, value]) => ({ name: type, value }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
            {t('financialReport.chartMixTitle', { defaultValue: 'Deposit Mix by Payment Type' })}
          </h2>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2}>
                {donutData.map((d) => (
                  <Cell key={d.name} fill={PM_TYPE_COLORS[d.name] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number, n: string) => [fmtMoney(symbol, v), paymentMethodTypeLabel(n)]}
              />
              <Legend formatter={(value: string) => paymentMethodTypeLabel(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <Th tip={metricTip('paymentType')}>{t('financialReport.columnPaymentType', { defaultValue: 'Type' })}</Th>
                {dimensions.includes('payment_method') && <Th tip={metricTip('paymentMethod')}>{t('financialReport.columnPaymentMethod', { defaultValue: 'Method' })}</Th>}
                {dimensions.includes('site') && <Th tip={metricTip('site')}>{t('financialReport.columnSite')}</Th>}
                <Th tip={metricTip('deposits')}>{t('financialReport.columnAmount')}</Th>
                <Th tip={metricTip('pctOfTotal')}>{t('financialReport.columnPctOfTotal', { defaultValue: '% of Total' })}</Th>
                <Th tip={metricTip('transactionCount')}>{t('financialReport.columnCount')}</Th>
                <Th tip={metricTip('uniquePlayers')}>{t('financialReport.columnUniqueDepositors')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noDataAvailable')}</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="bg-white dark:bg-slate-900">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(r.payment_method_type)}`}>
                      {paymentMethodTypeLabel(r.payment_method_type)}
                    </span>
                  </td>
                  {dimensions.includes('payment_method') && <td className="px-4 py-3 text-slate-900 dark:text-white">{r.payment_method || ''}</td>}
                  {dimensions.includes('site') && <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>}
                  <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{fmtMoney(symbol, r.total_amount)}</td>
                  <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber((r.pct_of_total || 0) * 100, 1)}%</td>
                  <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(r.transaction_count || 0)}</td>
                  <td className="px-4 py-3 text-slate-900 dark:text-white">
                    {(r.unique_players || 0) > 0 ? (
                      <DrillLink
                        title={t('financialReport.drillDownPlayers', { defaultValue: 'Show players' })}
                        onClick={() => setDrillScope({
                          dateFrom: filters.dateFrom,
                          dateTo: filters.dateTo,
                          siteIds: r.site_id != null ? [r.site_id] : filters.siteIds,
                          currencyMode: filters.currencyMode,
                          pmType: (r.payment_method_type as DrillDownScope['pmType']) || filters.pmType,
                          eventTypes: ['deposit_completed'],
                          title: `${paymentMethodTypeLabel(r.payment_method_type)} · ${t('financialReport.columnUniqueDepositors', { defaultValue: 'Players' })}`,
                        })}
                      >
                        {formatNumber(r.unique_players || 0)}
                      </DrillLink>
                    ) : formatNumber(r.unique_players || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DrillDownModal open={drillScope !== null} onClose={() => setDrillScope(null)} scope={drillScope} />
    </div>
  );
};

// ---------- FTD ----------

interface FTDRow {
  date: string;
  site_id: number;
  payment_method_type?: string;
  ftd_count: number;
  ftd_amount: number;
  avg_ftd_value: number;
}

export const FinancialFTDPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  const [drillScope, setDrillScope] = useState<DrillDownScope | null>(null);
  const { data, loading } = useFetch<{ data: FTDRow[] }>(
    () => getFTDReport({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      payment_method_type: filters.pmType,
      granularity: filters.granularity,
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.pmType, filters.granularity],
  );
  const rows = data?.data ?? [];
  const totalCount = rows.reduce((s, r) => s + (r.ftd_count || 0), 0);
  const totalAmount = rows.reduce((s, r) => s + (r.ftd_amount || 0), 0);
  const avg = totalCount > 0 ? totalAmount / totalCount : 0;

  // Aggregate per date for the line chart.
  const byDate = new Map<string, { date: string; ftd_count: number; ftd_amount: number }>();
  for (const r of rows) {
    const prev = byDate.get(r.date) ?? { date: r.date, ftd_count: 0, ftd_amount: 0 };
    prev.ftd_count += r.ftd_count || 0;
    prev.ftd_amount += r.ftd_amount || 0;
    byDate.set(r.date, prev);
  }
  const chartData = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard label={t('financialReport.kpiFTDCount',    { defaultValue: 'New Depositors' })} value={formatNumber(totalCount)} accent="blue" />
        <KpiCard label={t('financialReport.kpiFTDAmount',   { defaultValue: 'FTD Amount' })}     value={fmtMoney(symbol, totalAmount)} accent="green" />
        <KpiCard label={t('financialReport.kpiAvgFTDValue', { defaultValue: 'Avg FTD Value' })}  value={fmtMoney(symbol, avg)} accent="green" />
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
          {t('financialReport.chartFTDTrendTitle', { defaultValue: 'FTD Trend' })}
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <YAxis yAxisId="left"  stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }} />
            <Legend />
            <Line yAxisId="left"  type="monotone" dataKey="ftd_count"  name={t('financialReport.columnFTDCount',  { defaultValue: 'FTD Count' })}  stroke="#3b82f6" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="ftd_amount" name={t('financialReport.columnFTDAmount', { defaultValue: 'FTD Amount' })} stroke="#10b981" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: t('financialReport.columnDate'), i: 'date' }, { l: t('financialReport.columnSite'), i: 'site' },
                { l: t('financialReport.columnPaymentType', { defaultValue: 'Type' }), i: 'paymentType' },
                { l: t('financialReport.columnFTDCount', { defaultValue: 'FTD Count' }), i: 'ftdCount' },
                { l: t('financialReport.columnFTDAmount', { defaultValue: 'FTD Amount' }), i: 'ftdAmount' },
                { l: t('financialReport.kpiAvgFTDValue', { defaultValue: 'Avg FTD' }), i: 'avgFtd' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noDataAvailable')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(r.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodTypeBadgeClasses(r.payment_method_type)}`}>
                    {paymentMethodTypeLabel(r.payment_method_type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">
                  {(r.ftd_count || 0) > 0 ? (
                    <DrillLink
                      title={t('financialReport.drillDownFTD', { defaultValue: 'Show first-time depositors' })}
                      onClick={() => setDrillScope({
                        dateFrom: r.date,
                        dateTo: r.date,
                        siteIds: [r.site_id],
                        currencyMode: filters.currencyMode,
                        pmType: (r.payment_method_type as DrillDownScope['pmType']) || filters.pmType,
                        eventTypes: ['deposit_completed'],
                        ftdOnly: true,
                        title: `${siteName(allSites, r.site_id)} · ${r.date} · FTD ${paymentMethodTypeLabel(r.payment_method_type)}`,
                      })}
                    >
                      {formatNumber(r.ftd_count || 0)}
                    </DrillLink>
                  ) : formatNumber(r.ftd_count || 0)}
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(symbol, r.ftd_amount)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(symbol, r.avg_ftd_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DrillDownModal open={drillScope !== null} onClose={() => setDrillScope(null)} scope={drillScope} />
    </div>
  );
};

// ---------- Players ----------

interface PlayerRow {
  player_id: string;
  site_id: number;
  total_deposits: number;
  total_withdrawals: number;
  net_cash: number;
  deposit_count: number;
  withdrawal_count: number;
  first_deposit_at?: string;
  last_activity_at?: string;
}

export const FinancialPlayersPanel: React.FC<{ filters: FinancialFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const symbol = currencySymbol(filters.currencyMode);
  // Debounce the player_search so every keystroke doesn't hit the API.
  const [searchInput, setSearchInput] = useState(filters.playerSearch);
  useEffect(() => { setSearchInput(filters.playerSearch); }, [filters.playerSearch]);
  useEffect(() => {
    const h = setTimeout(() => {
      if (searchInput !== filters.playerSearch) filters.setPlayerSearch(searchInput);
    }, 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const { data, loading } = useFetch<{ data: PlayerRow[] }>(
    () => getPlayerCashier({
      date_from: filters.dateFrom, date_to: filters.dateTo,
      site_ids: siteParam(allSites, filters.siteIds),
      display_currency: filters.currencyMode,
      player_search: filters.playerSearch || undefined,
      limit: 100,
    }),
    [filters.dateFrom, filters.dateTo, filters.siteIds.join(','), filters.currencyMode, filters.playerSearch],
  );
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('financialReport.searchPlayerPlaceholder', { defaultValue: 'Search player_id…' })}
        </label>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="e.g. 12345"
          className="mt-1 w-full max-w-md rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[
                { l: t('financialReport.columnPlayerId', { defaultValue: 'Player ID' }), i: 'player' },
                { l: t('financialReport.columnSite'), i: 'site' },
                { l: t('financialReport.kpiTotalDeposits', { defaultValue: 'Deposits' }), i: 'deposits' },
                { l: t('financialReport.kpiTotalWithdrawals', { defaultValue: 'Withdrawals' }), i: 'withdrawals' },
                { l: t('financialReport.columnNetCash', { defaultValue: 'Net Cash' }), i: 'netCash' },
                { l: t('financialReport.columnCount') + ' D/W', i: 'transactionCount' },
                { l: t('financialReport.columnFirstDeposit', { defaultValue: 'First Deposit' }), i: '' },
                { l: t('financialReport.columnLastActivity', { defaultValue: 'Last Activity' }), i: '' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('financialReport.loadingPlayers', { defaultValue: 'Loading players…' })}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('financialReport.noPlayerResults', { defaultValue: 'No players match the filters.' })}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link to={`/reports/players/detail?player_id=${encodeURIComponent(r.player_id)}&site_id=${r.site_id}`}
                    className="font-medium text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                    {r.player_id}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={r.site_id} /></td>
                <td className="px-4 py-3 text-emerald-700 dark:text-emerald-400">{fmtMoney(symbol, r.total_deposits)}</td>
                <td className="px-4 py-3 text-red-700 dark:text-red-400">{fmtMoney(symbol, r.total_withdrawals)}</td>
                <td className={`px-4 py-3 font-semibold ${r.net_cash >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-red-700 dark:text-red-400'}`}>{fmtMoney(symbol, r.net_cash)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{r.deposit_count || 0} / {r.withdrawal_count || 0}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{r.first_deposit_at || ''}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{r.last_activity_at || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
